import { ApiClientError } from "./ApiClientError";
import {
  client,
  defaultTransport,
  ResponseError,
  Transport,
} from "./generated";

export type ClientGlobalConfig = {
  authToken?: string;
  baseURL?: string;
};

type UnauthorizedHandler = () => Promise<string | undefined>;

let authInterceptorId: number | undefined;
let errorInterceptorId: number | undefined;
let responseInterceptorId: number | undefined;
let currentAuthToken: string | undefined;
let unauthorizedHandler: UnauthorizedHandler | undefined;
let pendingRefresh: Promise<string | undefined> | undefined;

/**
 * Registers the callback used to get a fresh token when a request returns a 401.
 * It should resolve with the new access token, or `undefined` if renewal failed.
 */
export function setUnauthorizedHandler(
  handler: UnauthorizedHandler | undefined,
) {
  unauthorizedHandler = handler;
}

function refreshAuthToken() {
  if (!unauthorizedHandler) return Promise.resolve(undefined);
  // Concurrent 401s share a single renewal.
  pendingRefresh ??= unauthorizedHandler()
    .catch(() => undefined)
    .then((token) => {
      if (token) currentAuthToken = token;
      return token;
    })
    .finally(() => {
      pendingRefresh = undefined;
    });
  return pendingRefresh;
}

const authRefreshTransport: Transport = async (request) => {
  const result = await defaultTransport(request);
  if (result.status !== 401) return result;

  const usedAuthorization = request.headers.Authorization;
  // The token may already have been renewed while this request was in flight.
  const token =
    currentAuthToken && usedAuthorization !== `Bearer ${currentAuthToken}`
      ? currentAuthToken
      : await refreshAuthToken();
  if (!token) return result;

  return defaultTransport({
    ...request,
    headers: { ...request.headers, Authorization: `Bearer ${token}` },
  });
};

function installAuth() {
  if (authInterceptorId !== undefined) return;
  authInterceptorId = client.interceptors.request.use((request) => {
    if (currentAuthToken) {
      request.headers.Authorization = `Bearer ${currentAuthToken}`;
    }
    return request;
  });
  client.setConfig({ transport: authRefreshTransport });
}

function installErrorMapping() {
  if (errorInterceptorId !== undefined) return;
  errorInterceptorId = client.interceptors.error.use((error) => {
    const wrapped = new ApiClientError(error.status, error.data, error.message);
    wrapped.stack = error.stack;
    throw wrapped;
  });
}

function installResponseNormalization() {
  if (responseInterceptorId !== undefined) return;
  responseInterceptorId = client.interceptors.response.use((result) => {
    if (result.data === undefined) {
      result.data = null;
    }
    return result;
  });
}

export const setClientGlobalConfig = ({
  authToken,
  baseURL,
}: ClientGlobalConfig) => {
  client.setConfig({
    baseURL,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });

  currentAuthToken = authToken;

  installAuth();
  installErrorMapping();
  installResponseNormalization();
};

installAuth();
installErrorMapping();
installResponseNormalization();

export { ResponseError };
